import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CleaningBehavior } from 'src/swagger/humanresources/models/cleaning-behavior';
import { RolloutScheduleBehavior } from 'src/swagger/humanresources/models/rollout-schedule-behavior';
import { DEFAULT_ROLLOUT_BEHAVIOR_SELECTION, ROLLOUT_KEEP_OPTION } from './rollout-behavior.const';
import { RolloutBehaviorForm, RolloutBehaviorSelection } from './rollout-behavior.interface';

/**
 * Translates the planner's choice into the backend behavior object.
 *
 * Keeping absences also sets `keepVacations`: a vacation is an absence for the planner, but the
 * backend only guarantees full-day vacations through `keepVacations`.
 */
export function toRolloutScheduleBehavior(
  selection: RolloutBehaviorSelection,
): RolloutScheduleBehavior {
  if (selection.overwriteAll) {
    return {
      cleaning: CleaningBehavior.CleanAllEntries,
      keepVacations: false,
      keepAbsences: false,
    };
  }

  const keepAbsences = selection.keep.includes(ROLLOUT_KEEP_OPTION.Absences);

  return {
    cleaning: selection.keep.includes(ROLLOUT_KEEP_OPTION.ApprovedRequests)
      ? CleaningBehavior.CleanUnapprovedEntries
      : CleaningBehavior.CleanAllEntries,
    keepVacations: keepAbsences || selection.keep.includes(ROLLOUT_KEEP_OPTION.Vacations),
    keepAbsences,
  };
}

/** "Not everything" with nothing kept would overwrite everything — that state is invalid. */
export function isRolloutBehaviorSelectionValid(selection: RolloutBehaviorSelection): boolean {
  return selection.overwriteAll || selection.keep.length > 0;
}

export function createRolloutBehaviorForm(
  selection: RolloutBehaviorSelection = DEFAULT_ROLLOUT_BEHAVIOR_SELECTION,
): FormGroup<RolloutBehaviorForm> {
  return new FormGroup<RolloutBehaviorForm>({
    overwriteAll: new FormControl(selection.overwriteAll, { nonNullable: true }),
    keep: new FormControl([...selection.keep], {
      nonNullable: true,
      validators: Validators.required,
    }),
  });
}
